import { useQuery } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
import type { Environment } from '@/lib/api'

interface Props {
  environments: readonly Environment[]
  onToggle: (environment: Environment) => void
  onReset: () => void
}

/**
 * The global filter bar's environment selector.
 *
 * Story acceptance: the selector allows switching to staging, test or
 * development explicitly, and **the active non-production choice is visually
 * flagged**. The flag reads `non_production_active` from the API response
 * rather than deriving it locally, so the banner and the numbers can never
 * disagree about what is being shown.
 */
export function EnvironmentFilterBar({ environments, onToggle, onReset }: Props) {
  const client = useApiClient()
  const selector = useQuery({
    queryKey: ['environment-selector', client.kind, [...environments].sort()],
    queryFn: () => client.environmentSelector(environments),
  })

  const options = selector.data?.options ?? []
  const active = selector.data?.active ?? ['production']
  const flagged = selector.data?.non_production_active ?? false

  return (
    <div className="environment-filter" data-non-production={flagged}>
      <span className="environment-filter__label">Environment</span>
      <div className="environment-filter__options" role="group" aria-label="Environment filter">
        {options.map((option) => {
          const selected = active.includes(option.value)
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={selected}
              data-environment={option.value}
              data-selected={selected}
              disabled={option.website_count === 0 && !selected}
              onClick={() => onToggle(option.value)}
            >
              {option.label}
              <span className="environment-filter__count"> ({option.website_count})</span>
            </button>
          )
        })}
      </div>

      {flagged ? (
        <p role="status" className="environment-filter__warning" data-testid="non-production-warning">
          Showing non-production data. These figures include{' '}
          {active.filter((value) => value !== 'production').join(', ')} traffic and are not your
          reportable KPIs.{' '}
          <button type="button" onClick={onReset}>
            Back to production only
          </button>
        </p>
      ) : null}
    </div>
  )
}
